import React, { useState, useEffect } from 'react';
import PatientDashboardLayout from './PatientDashboardLayout';
import localForage from 'localforage';
import { useBaseUrl } from '../utils/utils';

interface Provider {
    id: string;
    name: string;
    specialty: string;
    clinic: string;
    email: string;
    phone: string;
    status: 'active' | 'pending' | 'inactive';
    avatar: string;
    rating: number;
}

interface CollaborationRequest {
    id: string;
    provider_name: string;
    provider_specialty: string;
    clinic_name: string;
    request_type: 'consultation' | 'referral' | 'care_team';
    status: 'pending' | 'approved' | 'rejected';
    message: string;
    created_at: string;
}

const PatientServiceRequests: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [providers, setProviders] = useState<Provider[]>([]);
    const [requests, setRequests] = useState<CollaborationRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [currentTab, setCurrentTab] = useState('providers');

    useEffect(() => {
        fetchCollaborations();
    }, []);

    const fetchCollaborations = async () => {
        setLoading(true);
        try {
            const email = await localForage.getItem<string>('patient_email');
            const response = await fetch(`${baseUrl}patient/api/collaborations/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_email: email || ''
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setProviders(data.data.providers || []);
                setRequests(data.data.requests || []);
            } else {
                setProviders([]);
                setRequests([]);
                setLoadError(data.message || 'Could not load your collaborations.');
            }
        } catch (error) {
            console.error('Failed to fetch collaborations:', error);
            setProviders([]);
            setRequests([]);
            setLoadError('Network error while loading your collaborations.');
        } finally {
            setLoading(false);
        }
    };

    const handleRequestAction = async (requestId: string, action: 'approve' | 'reject') => {
        try {
            const email = await localForage.getItem<string>('patient_email');
            const response = await fetch(`${baseUrl}patient/api/collaborations/request-action/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    request_id: requestId,
                    action: action,
                    user_email: email || ''
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                fetchCollaborations(); // Refresh data
                alert(`Request ${action}d successfully!`);
            } else {
                alert(`Failed to ${action} request: ${data.message}`);
            }
        } catch (error) {
            console.error('Request action error:', error);
            alert(`Failed to ${action} request. Please try again.`);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'inactive': return 'bg-gray-100 text-gray-800';
            default: return 'bg-blue-100 text-blue-800';
        }
    };

    const getRequestStatusColor = (status: string) => {
        switch (status) {
            case 'approved': return 'bg-green-100 text-green-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-KE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <PatientDashboardLayout>
                <div className="p-6">
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-lg text-gray-600">Loading service requests...</span>
                    </div>
                </div>
            </PatientDashboardLayout>
        );
    }

    return (
        <PatientDashboardLayout>
            <div className="p-6 max-w-4xl mx-auto space-y-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row items-center gap-4 pb-4 border-b border-gray-200 mb-6">
                    <div className="flex items-center gap-4">
                        <img src={`${process.env.PUBLIC_URL}/assets/images/collaborations.png`} alt="Service Requests" className="w-12 h-12 rounded-full border-4 border-blue-200 shadow" />
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-1">My Service Requests</h2>
                            <p className="text-gray-500">Manage your healthcare service requests and provider interactions</p>
                        </div>
                    </div>
                </div>
                {loadError && (
                    <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">{loadError}</div>
                )}
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="card shadow border-0 rounded-2xl p-6 bg-white flex flex-col items-center">
                        <div className="p-3 bg-blue-50 rounded-xl mb-2">
                            <i className="ri-user-heart-line text-blue-600 text-xl"></i>
                        </div>
                        <p className="text-sm font-medium text-gray-600">Active Providers</p>
                        <p className="text-2xl font-bold text-gray-900">{providers.filter(p => p.status === 'active').length}</p>
                    </div>
                    <div className="card shadow border-0 rounded-2xl p-6 bg-white flex flex-col items-center">
                        <div className="p-3 bg-yellow-50 rounded-xl mb-2">
                            <i className="ri-time-line text-yellow-600 text-xl"></i>
                        </div>
                        <p className="text-sm font-medium text-gray-600">Pending Requests</p>
                        <p className="text-2xl font-bold text-gray-900">{requests.filter(r => r.status === 'pending').length}</p>
                    </div>
                    <div className="card shadow border-0 rounded-2xl p-6 bg-white flex flex-col items-center">
                        <div className="p-3 bg-green-50 rounded-xl mb-2">
                            <i className="ri-team-line text-green-600 text-xl"></i>
                        </div>
                        <p className="text-sm font-medium text-gray-600">Care Team</p>
                        <p className="text-2xl font-bold text-gray-900">{providers.filter(p => p.status === 'active').length}</p>
                    </div>
                </div>
                {/* Tabs */}
                <div className="card shadow border-0 rounded-2xl bg-white">
                    <div className="border-b border-gray-200 px-6 pt-4">
                        <nav className="flex flex-wrap gap-4">
                            <button
                                onClick={() => setCurrentTab('providers')}
                                className={`py-2 px-4 rounded-t-lg font-medium text-sm focus:outline-none transition-colors ${
                                    currentTab === 'providers'
                                        ? 'bg-blue-100 text-blue-700 shadow'
                                        : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                                }`}
                            >
                                My Providers
                            </button>
                            <button
                                onClick={() => setCurrentTab('requests')}
                                className={`py-2 px-4 rounded-t-lg font-medium text-sm focus:outline-none transition-colors ${
                                    currentTab === 'requests'
                                        ? 'bg-blue-100 text-blue-700 shadow'
                                        : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                                }`}
                            >
                                Service Requests
                            </button>
                            <button
                                onClick={() => setCurrentTab('team')}
                                className={`py-2 px-4 rounded-t-lg font-medium text-sm focus:outline-none transition-colors ${
                                    currentTab === 'team'
                                        ? 'bg-blue-100 text-blue-700 shadow'
                                        : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                                }`}
                            >
                                Care Team
                            </button>
                        </nav>
                    </div>
                    <div className="p-6">
                        {currentTab === 'providers' && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {providers.map((provider) => (
                                        <div key={provider.id} className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                                            <div className="flex items-center mb-4">
                                                <img 
                                                    src={`${process.env.PUBLIC_URL}${provider.avatar}`} 
                                                    alt={provider.name}
                                                    className="w-16 h-16 rounded-full object-cover"
                                                />
                                                <div className="ml-4">
                                                    <h3 className="font-semibold text-gray-900">{provider.name}</h3>
                                                    <p className="text-sm text-gray-600">{provider.specialty}</p>
                                                    <div className="flex items-center mt-1">
                                                        <div className="flex text-yellow-400">
                                                            {[...Array(5)].map((_, i) => (
                                                                <i key={i} className={`ri-star-${i < Math.floor(provider.rating) ? 'fill' : 'line'}`}></i>
                                                            ))}
                                                        </div>
                                                        <span className="ml-2 text-sm text-gray-600">{provider.rating}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <p className="text-sm text-gray-600">
                                                    <i className="ri-building-line mr-2"></i>
                                                    {provider.clinic}
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    <i className="ri-mail-line mr-2"></i>
                                                    {provider.email}
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    <i className="ri-phone-line mr-2"></i>
                                                    {provider.phone}
                                                </p>
                                            </div>
                                            <div className="mt-4 flex justify-between items-center">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(provider.status)}`}>
                                                    {provider.status}
                                                </span>
                                                <div className="flex space-x-2">
                                                    <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors">
                                                        Message
                                                    </button>
                                                    <button className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors">
                                                        View Profile
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {currentTab === 'requests' && (
                            <div className="space-y-4">
                                {requests.map((request) => (
                                    <div key={request.id} className="border border-gray-200 rounded-xl p-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <h3 className="font-semibold text-gray-900">{request.provider_name}</h3>
                                                <p className="text-sm text-gray-600">{request.provider_specialty} • {request.clinic_name}</p>
                                                <p className="text-sm text-gray-500 mt-1">{formatDate(request.created_at)}</p>
                                            </div>
                                            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getRequestStatusColor(request.status)}`}>
                                                {request.status}
                                            </span>
                                        </div>
                                        <p className="text-gray-700 mb-4">{request.message}</p>
                                        {request.status === 'pending' && (
                                            <div className="flex space-x-3">
                                                <button
                                                    onClick={() => handleRequestAction(request.id, 'approve')}
                                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    onClick={() => handleRequestAction(request.id, 'reject')}
                                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {currentTab === 'team' && (
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Care Team</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {providers.filter(p => p.status === 'active').map((provider) => (
                                            <div key={provider.id} className="bg-gray-50 rounded-xl p-6">
                                                <div className="flex items-center mb-4">
                                                    <img 
                                                        src={`${process.env.PUBLIC_URL}${provider.avatar}`} 
                                                        alt={provider.name}
                                                        className="w-12 h-12 rounded-full object-cover"
                                                    />
                                                    <div className="ml-3">
                                                        <h4 className="font-medium text-gray-900">{provider.name}</h4>
                                                        <p className="text-sm text-gray-600">{provider.specialty}</p>
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <p className="text-sm text-gray-600">{provider.clinic}</p>
                                                    <div className="flex space-x-2">
                                                        <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors">
                                                            Contact
                                                        </button>
                                                        <button className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors">
                                                            Schedule
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PatientDashboardLayout>
    );
};

export default PatientServiceRequests; 